import { mapActions, mapState } from 'vuex';
import panel from '~/mixins/panel';

import IMask from 'imask';
import '~/utils/vue-formulate';
import formulateUserProfile from '~/mixins/formulateUserProfile';
import autocompletesFormulateForm from '~/mixins/autocompletesFormulateForm';
import receivesFieldsFromFormulateSchema from '~/mixins/receivesFieldsFromFormulateSchema';
import withAnalytics from '~/mixins/withAnalytics';
import agreementsMixin from '~/mixins/agreementsMixin';
import loyaltyPoints from '~/mixins/loyaltyPoints';
import { createMaskAmount } from '~/utils/intl-converter';

import { FORM_PAYER_TYPE } from '~/utils/dictionaries';

import moment from '@/utils/moment';

const locale = 'Ru-ru';
const rub = new Intl.NumberFormat(locale, {
	style: 'currency',
	currency: 'RUB',
	minimumFractionDigits: 0,
});

export default {
	mixins: [
		panel,
		formulateUserProfile,
		autocompletesFormulateForm,
		receivesFieldsFromFormulateSchema,
		withAnalytics,
		agreementsMixin,
		loyaltyPoints,
	],
	provide() {
		const formValues = {};

		Object.defineProperty(formValues, 'values', {
			enumerable: true,
			get: () => this.formValues,
		});

		return {
			formValues,
			setFormValues: (field, value) => {
				this.$set(this.formValues, field, value);
			},
		};
	},
	data() {
		return {
			formValues: {
				company_name: '',
				residence_city: '',
				// New Data
				payer: {
					type: FORM_PAYER_TYPE.PHYSICAL,
				},
				is_representative: false,
				representative: {
					first_name: '',
					last_name: '',
					phone: '',
					email: '',
				},
			},
			schema: [],
			isLoadingSubmit: false,
			can_return_vat_selected: false,
			delayed_payment: false,
			selectedProducts: [],
			isAuthorized: false,
			mounted: false,
			fullCompany: null,
			promocode: '',
			promocodeMessage: null,
			promocodeButtonText: 'Применить',
			isLoadingPromocode: false,
			productsInput: {},
			products: [],
			isActivatePromocode: false,
			priceWithPromocode: null,
			priceWithPromocodeWithoutDiscount: null,
			errorMessage: null,
			discount: false,
			isErrorCookie: false,
			isValidPhone: false,
			okSuccessMessage: null,
		};
	},
	computed: {
		...mapState('cookiesDisclaimer', ['isCookiesAccepted']),
		amount() {
			return this.allProductsByType?.reduce(
				(sum, product) => parseFloat(sum) + parseFloat(product?.price ?? 0) * (parseFloat(product?.quantity ?? 1) || 0),
				0,
			);
		},
		discountPrice() {
			return this.allProductsByType?.reduce(
				(sum, product) => parseFloat(sum) + parseFloat(product?.discount_price ?? 0) * (parseFloat(product?.quantity ?? 1) || 0),
				0,
			);
		},
		allProductsByType() {
			let products = [];
			if (this.block.products_list_type) {
				products = this.selectedProducts;
			} else {
				products = this.allProductsFromGroup;
			}
			return products;
		},
		allProductsFromGroup() {
			if (this.groups.length === 0) {
				return this.block.products;
			}

			let products = [];

			this.groups.forEach((group) => {
				products = [...group?.formulate_products, ...products];
			});

			return products;
		},
		groups() {
			return this.block.groups.filter((group) => group !== null);
		},
		isFormClosed() {
			return this.block.products.length > 0 ? this.block.products.every((product) => {
				if (product.is_quantity_restriction_enabled === 1) {
					return product.quantity_restriction <= product.attached_count;
				}
				return false;
			}) : true;
		},
	},
	watch: {
		'$auth.user': {
			async handler(user) {
				if (user) {
					this.isAuthorized = true;

					const fields = await this.receiveFieldsFromSchema(this.schema);
					fields.push({ name: 'birthday' });
					this.getAutocompletedFields(fields, true).then((res) => {
						this.formValues = { ...this.formValues, ...res };
					});
				}
			},
			immediate: true,
		},
		products: {
			handler(value) {
				if (value) {
					const selectedProducts = [];
					if (Array.isArray(value.products)) {
						value.products.forEach((product) => {
							const currentProduct = product.products ?? product;

							if (
								typeof currentProduct === 'string'
								|| currentProduct instanceof String
							) {
								selectedProducts.push(
									this.allProductsFromGroup.find(
										(p) => p.id
											=== parseInt(currentProduct, 10),
									),
								);
							}
							if (Array.isArray(currentProduct)) {
								currentProduct.forEach((anotherProduct) => {
									selectedProducts.push(
										this.allProductsFromGroup.find(
											(p) => p.id
												=== parseInt(anotherProduct, 10),
										),
									);
								});
							}
							if (currentProduct instanceof Object) {
								selectedProducts.push(currentProduct);
							}
						});
					}

					if (
						typeof value.products === 'string'
						|| value.products instanceof String
						|| typeof value.products === 'number'
					) {
						selectedProducts.push(
							this.allProductsFromGroup.find(
								(p) => p.id === parseInt(value.products, 10),
							),
						);
					}

					this.selectedProducts = selectedProducts;
				}

				if (value.userData) {
					this.formValues = { ...this.formValues, ...value.userData };
				}
			},
			immediate: true,
		},
		'block.schema': {
			async handler(schema) {
				this.schema = [...schema];
				const fields = await this.receiveFieldsFromSchema(this.schema);
				fields.push({ name: 'birthday' });
				this.getAutocompletedFields(fields).then((res) => {
					this.formValues = { ...this.formValues, ...res };
				});
			},

			immediate: true,
		},
		promocode() {
			this.promocodeMessage = null;
		},
		selectedProducts: {
			async handler() {
				await this.fetchAwardedPoints();
			},
			deep: true,
		},
		isCookiesAccepted: {
			handler(value) {
				if (value) {
					this.isErrorCookie = false;
				}
			},
			immediate: true,
		},
		isFormClosed: {
			handler(value) {
				if (value) {
					this.block.products = this.block.products.filter((product) => {
						if (product.is_quantity_restriction_enabled === 1) {
							return product.quantity_restriction !== product.attached_count;
						}
						return true;
					});
				}
			},
			immediate: true,
		},
	},
	created() {
		this.checkDate();
		this.makePhoneValidRule();
	},
	async mounted() {
		this.mounted = true;

		await this.fetchAwardedPoints();
	},
	methods: {
		createMaskAmount,
		...mapActions({
			showCookiesDisclaimer: 'cookiesDisclaimer/show',
			hideCookiesDisclaimer: 'cookiesDisclaimer/hide',
			showLoginPopup: 'authModal/showLogin',
			showRegistrationPopup: 'authModal/showRegistration',
		}),
		checkDate() {
			this.discount = false;
			const today = moment().endOf('day');
			this.allProductsByType.forEach((program) => {
				if (program.active_discount_to) {
					const active = program.active_discount_to;
					const check = moment(today).isSameOrBefore(active, 'day');
					if (check) {
						this.discount = true;
					}
				}
			});
		},
		async checkPromocode(products) {
			this.isLoadingPromocode = true;
			this.promocodeButtonText = 'Проверяется...';

			const data = {
				promocode: this.promocode,
				products,
				form_scenario_id: this.block.scenario_id,
				email: this.formValues.email,
			};

			await this.$api
				.checkPromocode(data)
				.then((response) => {
					if (response.success) {
						console.log(
							`price with promocode: ${this.promocode} - ${response.price}`,
						);
						this.priceWithPromocode = Math.floor(response.price);
						this.priceWithPromocodeWithoutDiscount = +response.price + +response.discount;
						this.promocodeMessage = null;
						this.isActivatePromocode = true;
						this.promocodeButtonText = 'Применить другой';
					} else {
						console.log(
							`check promocode failed with reason: ${response.reason}`,
						);
						this.promocodeMessage = response.reason;
						this.isActivatePromocode = false;
						this.promocodeButtonText = 'Применить';
					}
					this.isLoadingPromocode = false;
				})
				.catch((e) => {
					console.log(e);
					this.promocodeButtonText = 'Применить';
					this.promocodeMessage = 'Произошла ошибка';
					this.isActivatePromocode = false;
					this.isLoadingPromocode = false;
				});
		},
		resetPromocode() {
			this.promocode = '';
			this.isActivatePromocode = false;
			this.priceWithPromocode = null;
			this.priceWithPromocodeWithoutDiscount = null;
		},
		setCompanyInfo(value) {
			this.fullCompany = value;
			this.formValues.inn = value?.data?.inn ?? null;
		},
		async submit(hash, callback, step) {
			this.isLoadingSubmit = true;
			this.errorMessage = null;

			if (!this.isCookiesAccepted) {
				this.showCookieError();
				this.isErrorCookie = true;
				this.isLoadingSubmit = false;

				return;
			}

			const data = this.getData();
			this.isErrorCookie = false;

			window.dataLayer = window.dataLayer || [];
			// eslint-disable-next-line no-undef
			dataLayer.push({
				event: 'form_send',
				form_data: data,
			});

			const formData = new FormData();

			this.setFilesForSend(formData);
			this.createFormData(formData, data);

			if (typeof hash === 'string') {
				callback(hash, formData, step);
				return;
			}

			await this.$api
				.submitFormulateForm(
					this.block.scenario_id,
					formData,
				)
				.then((response) => {
					let hashToken;
					if (response.url) {
						if (response.url.includes('https://')) {
							window.location = response.url;
						} else {
							// Логика получение хэша и автологина сценария с шагами
							const scenarioUserData = JSON.parse(response.url);
							hashToken = scenarioUserData.hash;

							// if (scenarioUserData.autologin_url) {
							// 	window.location = scenarioUserData.autologin_url;
							// 	return;
							// }
						}
					}
					this.okSuccessMessage = response.success_data;
					this.$emit('submit', hashToken);
				})
				.catch((error) => {
					this.isLoadingSubmit = false;
					if (error.response.status === 422) {
						if (error.response?.data?.details?.email) {
							this.errorMessage = error.response.data.details.email;
						} else {
							this.errorMessage = 'Вы уже подали заявку ранее. В ближайшее время с Вами свяжется консультант';
						}
						const { errors } = error.response.data;
						if (typeof errors === 'object') {
							Object.keys(errors).forEach((key) => {
								this.errorMessage = errors[key][0];
							});
						}
					}
				}).finally(() => {
					this.$emit('submitLoading', false);
				});
		},
		showCookieError() {
			this.isErrorCookie = true;

			this.hideCookiesDisclaimer().then(() => this.showCookiesDisclaimer());
		},
		setFilesForSend(formData) {
			const files = {};

			Object.keys(this.formValues).forEach((key) => {
				if (this.formValues[key]?.fileList) {
					files[key] = this.formValues[key].files.map(
						(el) => el.file,
					);

					/** TODO: подумать как можно сделать лучше,
					 *  TODO: чтобы файл в форме оставался,
					 *  TODO: на момент написания кода я так и не придумал.
					 *
					 *  это нужно для того,
					 *  чтобы функция создания formData не ломалась при виде файлов из Formulate
					 */
					this.formValues[key] = null;
				}
			});

			this.createFormData(formData, files, 'user_data');
		},
		getSelectedProducts() {
			return this.selectedProducts ?? null;
		},
		getUserData() {
			const userData = this.formValues;

			return {
				sklTargetAction: this.block.form_scenario_action_label,
				sklAgreeSubscribeLookup: this.formValues.advertisingMailingsAgreement ? 'да' : 'нет',
				sklBirthDate: this.formValues.birthday ? moment(this.formValues.birthday).format('DD.MM.YYYY') : '',
				...userData,
				...this.getAnalyticsData(),
				...{ can_return_vat_selected: +this.can_return_vat_selected },
				...this.getAdditionalUserData(),
			};
		},
		getAdditionalUserData() {
			return {};
		},
		getData() {
			if (!this.agreements.marketing_emails) {
				this.agreements.marketing_emails = !!this.formValues.advertisingMailingsAgreement;
			}
			if (!this.agreements.personal_data) {
				this.agreements.personal_data = !!this.formValues.personalData;
			}
			if (!this.formValues.is_representative) {
				this.formValues.representative = null;
			}
			this.setAgreementFields();

			return {
				user_data: this.getUserData(),
				products: this.getSelectedProducts(),
				entity_name: this.fetchedData.entity_name,
				entity_id: this.fetchedData.id,
			};
		},
		createFormData(formData, data, parentKey) {
			if (
				data
				&& typeof data === 'object'
				&& !(data instanceof Date)
				&& !(data instanceof File)
			) {
				if ('fileList' in data) {
					if (data.files.length === 1) {
						formData.append(parentKey, data.files[0].file);

						return;
					}

					Object.keys(data.files).forEach((uploadedKey) => {
						if (formData.has(`${parentKey}[${uploadedKey}]`)) {
							return;
						}

						formData.append(
							`${parentKey}[${uploadedKey}]`,
							data.files[uploadedKey].file,
						);
					});

					return;
				}

				Object.keys(data).forEach((key) => {
					this.createFormData(
						formData,
						data[key],
						parentKey ? `${parentKey}[${key}]` : key,
					);
				});
			} else if (!formData.has(parentKey)) {
				const value = data === null ? '' : data;

				formData.append(parentKey, value);
			}
		},
		onChangeSwitchMiddleName(e) {
			const middle = this.schemaFields.find(
				(el) => el.name === 'middle_name',
			);
			if (e) {
				this.formValues.middle_name = null;
				middle.validation = '';
			} else {
				if (this.formulateUserProfile.middle_name) {
					this.formValues.middle_name = this.formulateUserProfile.middle_name;
				}
				middle.validation = 'required';
			}
		},
		// createMaskAmount(value) {
		// 	const price = parseFloat(value);
		// 	return IMask.pipe(price.toString(), {
		// 		mask: 'num',
		// 		blocks: {
		// 			num: {
		// 				mask: Number,
		// 				thousandsSeparator: ' ',
		// 			},
		// 		},
		// 	});
		// 	return rub.format(value);
		// },
		setValidation(fieldName, validation) {
			const field = this.schemaFields.find(
				(el) => el.name === fieldName,
			);
			if (field) {
				field.validation = validation;
			}
		},
		setDisabled(fieldName, value) {
			const field = this.schemaFields.find(
				(el) => el.name === fieldName,
			);
			if (field) {
				field.disabled = value;
			}
		},
		validatePhone(val) {
			this.isValidPhone = val;
		},
		makePhoneValidRule() {
			// eslint-disable-next-line func-names
			this.$formulate.options.rules.validPhone = function () {
				return this.isValidPhone;
			}.bind(this);
		},
		onClickAgainSubmit() {
			this.okSuccessMessage = null;
			this.isLoadingSubmit = false;
		},
	},
};
